import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { environment } from '../../app/app.environment';
import { DynamicTemplateConfig } from './models/dynamic-template-config.interface';

/**
 * API Response interface for client template
 */
interface ClientTemplateApiResponse {
  status: number;
  message: string;
  data: {
    id: number;
    uuid: string;
    business_id: number;
    client_name: string;
    short_name: string;
    roles: string;
    businesstype: number;
    registrationtype: number;
    industrytype: number;
    establishedon: string;
    address: string;
    address2: string;
    city: number;
    state_id: number;
    country: number;
    noofwarehouse: number;
    noofstores: number;
    licenseperiod: string;
    trialperiod: number;
    licensetype: string;
    state_name: string;
    pincode: string;
    email: string;
    mobile: string;
    logo: string;
    max_comp_count: number;
    max_emp_count: number;
    gstno: string;
    pan_no: string;
    url: string;
    terms: string;
    signature: string | null;
    licenseno: string;
    msmeno?: string;
    msmetype?: string;
    msme_no?: string;
    msme_type?: string;
    createdAt: string;
    updatedAt: string;
    status: number;
    bankaccount: any;
    clienttemplate: {
      id: number;
      client_id: number;
      template_id: number;
      image: string;
      format: string;
      default: number;
      clientselected: number;
      status: number;
      templatejson: string; // This is the JSON string we need to parse
      createdAt: string;
      updatedAt: string;
    };
  };
}

/**
 * Service to fetch dynamic template configurations from API
 */
@Injectable({
  providedIn: 'root',
})
export class DynamicTemplateConfigService {
  apiUrl = environment.apiURL + 'v1';

  private cachedConfig: DynamicTemplateConfig | null = null;
  private isConfigLoaded = false;
  private cachedBankAccount: any = null;
  private cachedLogo: string = '';
  private cachedClientData: any = null; // Cache full client data including gstno
  /** True while /sale/client is in flight, so getClientData() doesn't fire duplicate loads */
  private clientSaleDataLoading = false;
  /** Last background load started by getClientData(); limits retries if the API keeps failing */
  private lastClientSaleDataAttempt = 0;

  constructor(private http: HttpClient) { }

  /**
   * Preload template configuration from API
   */
  preloadTemplateConfig(
    clientId: number,
  ): Observable<DynamicTemplateConfig | null> {
    return this.http
      .get<ClientTemplateApiResponse>(`${this.apiUrl}/sale/client/${clientId}`)
      .pipe(
        map((response) => {
          if (response.status !== 200 || !response.data) {
            return null;
          }
          console.log(response.data, 'Business Detail Values (GST, Address, PAN)')
          this.cachedBankAccount = response.data.bankaccount;
          this.cachedLogo = response.data.logo || '';
          this.cachedClientData = response.data; // Cache full client data

          const templateJsonString = response.data.clienttemplate?.templatejson;
          console.log(JSON.parse(templateJsonString),'template json')

          if (!templateJsonString) {
            return null;
          }

          try {
            const config = JSON.parse(
              templateJsonString,
            ) as DynamicTemplateConfig;
            this.cachedConfig = config;
            this.isConfigLoaded = true;
            return config;
          } catch (error) {
            console.error(
              '[DynamicTemplateConfigService] Error parsing templatejson:',
              error,
            );
            return null;
          }
        }),
        catchError((error) => {
          console.error(
            '[DynamicTemplateConfigService] API error during preload:',
            error,
          );
          return of(null);
        }),
      );
  }

  /**
   * Get template configuration (uses cached config if available)
   * @param templateId - Not used in current implementation, but kept for future use
   */
  getTemplateConfig(templateId: number): Observable<DynamicTemplateConfig> {
    if (this.isConfigLoaded && this.cachedConfig) {
      // check cached - w are loding on launch
      return of(this.cachedConfig);
    }

    const clientId = this.getClientId();

    if (!clientId) {
      console.error(
        '[DynamicTemplateConfigService] No client_id found in localStorage',
      );
      return throwError(() => new Error('Client ID not found'));
    }

    console.log(
      `[DynamicTemplateConfigService] Fetching template configuration for client_id: ${clientId}`,
    );

    return this.http
      .get<ClientTemplateApiResponse>(`${this.apiUrl}/sale/client/${clientId}`)
      .pipe(
        map((response) => {
          if (response.status !== 200 || !response.data) {
            throw new Error('Invalid API response');
          }

          this.cachedBankAccount = response.data.bankaccount;
          this.cachedLogo = response.data.logo || '';
          this.cachedClientData = response.data; // Cache full client data

          const templateJsonString = response.data.clienttemplate?.templatejson;

          if (!templateJsonString) {
            console.warn(
              '[DynamicTemplateConfigService] No templatejson found, using mock config',
            );
            return this.getMockConfig();
          }

          try {
            const config = JSON.parse(
              templateJsonString,
            ) as DynamicTemplateConfig;
            this.cachedConfig = config;
            this.isConfigLoaded = true;
            console.log(
              '[DynamicTemplateConfigService] Successfully parsed and cached template configuration',
            );
            return config;
          } catch (error) {
            console.error(
              '[DynamicTemplateConfigService] Error parsing templatejson:',
              error,
            );
            console.warn(
              '[DynamicTemplateConfigService] Falling back to mock config',
            );
            return this.getMockConfig();
          }
        }),
        catchError((error) => {
          console.error('[DynamicTemplateConfigService] API error:', error);
          console.warn(
            '[DynamicTemplateConfigService] Falling back to mock config',
          );
          return of(this.getMockConfig());
        }),
      );
  }

  /**
   * Converts an image URL (http/https) to a Base64 data URL.
   * If conversion fails, safely returns null without throwing errors.
   */
  private async convertUrlToBase64(url: string): Promise<string | null> {
    if (!url || typeof url !== 'string') return null;
    const trimmed = url.trim();
    if (trimmed.startsWith('data:image/')) return trimmed;
    if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) return null;

    try {
      const res = await fetch(trimmed, { mode: 'cors' });
      if (!res.ok) return null;
      const blob = await res.blob();
      return new Promise<string | null>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result;
          if (typeof result === 'string' && result.startsWith('data:image/')) {
            resolve(result);
          } else {
            resolve(null);
          }
        };
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      console.warn('[DynamicTemplateConfigService] Could not convert image URL to base64:', e);
      return null;
    }
  }

  /**
   * Fetch client data from /sale/client/{clientId}
   * Retrieves and caches: logo, signature, bankaccount details, and terms & conditions
   * Safely converts logo and signature URLs to Base64 data URLs in the background.
   */
  loadClientSaleData(clientId?: number | string): Observable<any> {
    const cId = clientId || this.getClientId();
    if (!cId) {
      return of(this.getClientData());
    }

    this.clientSaleDataLoading = true;
    return this.http
      .get<ClientTemplateApiResponse>(`${this.apiUrl}/sale/client/${cId}`)
      .pipe(
        finalize(() => (this.clientSaleDataLoading = false)),
        tap(async (response: ClientTemplateApiResponse) => {
          if (response && response.data) {
            const data = { ...response.data };
            this.cachedClientData = data;
            this.cachedBankAccount = data.bankaccount;
            this.cachedLogo = data.logo || '';

            // Save raw data immediately
            try {
              localStorage.setItem('client_sale_data', JSON.stringify(data));
              const msmeNo = data.msmeno || (data as any).msme_no || (data as any).msmeNo;
              const msmeType = data.msmetype || (data as any).msme_type || (data as any).msmeType;
              if (msmeNo) {
                localStorage.setItem('msmeno', msmeNo);
              }
              if (msmeType) {
                localStorage.setItem('msmetype', msmeType);
              }
            } catch (e) {}

            // Asynchronously convert logo and signature URLs to Base64 data URLs
            let changed = false;
            if (
              data.logo &&
              typeof data.logo === 'string' &&
              (data.logo.startsWith('http://') || data.logo.startsWith('https://'))
            ) {
              const base64Logo = await this.convertUrlToBase64(data.logo);
              if (base64Logo) {
                data.logo = base64Logo;
                this.cachedLogo = base64Logo;
                changed = true;
              }
            }

            if (
              data.signature &&
              typeof data.signature === 'string' &&
              (data.signature.startsWith('http://') || data.signature.startsWith('https://'))
            ) {
              const base64Sig = await this.convertUrlToBase64(data.signature);
              if (base64Sig) {
                data.signature = base64Sig;
                changed = true;
              }
            }

            if (changed) {
              this.cachedClientData = data;
              try {
                localStorage.setItem('client_sale_data', JSON.stringify(data));
              } catch (e) {}
            }
          }
        }),
        map((response) => response?.data || null),
        catchError((error) => {
          console.error(
            '[DynamicTemplateConfigService] Error loading client sale data:',
            error,
          );
          return of(this.getClientData());
        }),
      );
  }

  isConfigCached(): boolean {
    return this.isConfigLoaded && this.cachedConfig !== null;
  }

  /**
   * Get cached client data (includes logo, signature, bankaccount, terms, gstno, etc.)
   */
  getClientData(): any {
    const currentClientId = this.getClientId();

    if (this.cachedClientData) {
      if (this.belongsToClient(this.cachedClientData, currentClientId)) return this.cachedClientData;
      // Cached data is from a previously logged-in client
      this.resetClientSaleData();
    }

    try {
      const raw = localStorage.getItem('client_sale_data');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && this.belongsToClient(parsed, currentClientId)) {
          this.cachedClientData = parsed;
          this.cachedBankAccount = parsed.bankaccount;
          this.cachedLogo = parsed.logo || '';
          return this.cachedClientData;
        }
        localStorage.removeItem('client_sale_data');
      }
    } catch {}

    // Not loaded yet (fresh login, or storage cleared): fetch in the background so the
    // next call has it. Covers every login/clear path without callers having to know.
    if (currentClientId && !this.clientSaleDataLoading && Date.now() - this.lastClientSaleDataAttempt > 30000) {
      this.lastClientSaleDataAttempt = Date.now();
      this.loadClientSaleData(currentClientId).subscribe();
    }
    return null;
  }

  /**
   * Clears only the client sale data (logo, signature, bank, terms) from memory and storage.
   * Unlike clearCache(), the template config is kept.
   */
  resetClientSaleData(): void {
    this.lastClientSaleDataAttempt = 0;
    this.cachedClientData = null;
    this.cachedBankAccount = null;
    this.cachedLogo = '';
    try {
      localStorage.removeItem('client_sale_data');
    } catch {}
  }

  /** Data without an id (older cache) or with no logged-in client is accepted as-is */
  private belongsToClient(data: any, clientId: number | null): boolean {
    return clientId == null || data?.id == null || data.id == clientId;
  }

  getBankAccount(): any {
    if (this.cachedBankAccount) return this.cachedBankAccount;
    const client = this.getClientData();
    return client?.bankaccount || null;
  }

  getLogo(): string {
    if (this.cachedLogo) return this.cachedLogo;
    const client = this.getClientData();
    return client?.logo || '';
  }

  getSignature(): string | null {
    const client = this.getClientData();
    return client?.signature || null;
  }

  getTerms(): string {
    const client = this.getClientData();
    return client?.terms || '';
  }

  clearCache(): void {
    this.cachedConfig = null;
    this.isConfigLoaded = false;
    this.cachedBankAccount = null;
    this.cachedLogo = '';
    this.cachedClientData = null;
    try {
      localStorage.removeItem('client_sale_data');
    } catch {}
    console.log('[DynamicTemplateConfigService] Cache cleared');
  }

  private getClientId(): number | null {
    const clientIdStr = localStorage.getItem('client_id');
    if (!clientIdStr) {
      return null;
    }

    try {
      // Login stores JSON.stringify(user.client_id); the API may send it as a number or a numeric string
      const clientId = Number(JSON.parse(clientIdStr));
      return Number.isFinite(clientId) && clientId > 0 ? clientId : null;
    } catch {
      return null;
    }
  }

  /**
   * Mock configuration based on provided JSON
   */
  private getMockConfig(): DynamicTemplateConfig {
    return {
      additionalSettings: {
        autoApplyLuxuryTheme: false,
        currencyPosition: 'before',
        currencySymbol: '₹',
        dateFormat: 'DD-MM-YYYY',
        decimalPlaces: 2,
        enableFreeItemQuantity: false,
        priceHistory: false,
      },
      businessDetails: {
        alignment: 'left',
        businessNameFontSize: 22,
        emailLabel: 'E-Mail:',
        logoBase64: '',
        logoPosition: 'left',
        logoWidth: 80,
        phoneLabel: 'Tel:',
        showAddress: true,
        showBusinessName: true,
        showEmail: true,
        showGSTIN: true,
        showLogo: true,
        showPhone: true,
        showTagline: true,
        showWebsite: true,
        tagline: '',
        showMsmeNo: true,
        showMsmeType: true,
      },
      footer: {
        alignment: 'left',
        bankAccountId: 56,
        customerSealLabel: "Customer's Seal and Signature",
        declarationLabel: 'Declaration',
        declarationText:
          'We declare that this invoice shows actual price of the goods described and that all particulars are true and correct.',
        narrationLabel: '',
        narrationText: '',
        panLabel: "Company's PAN :",
        qrCodePosition: 'right',
        receiverSignatureLabel: "Buyer's Signature",
        showBankDetails: true,
        showCustomerSeal: true,
        showDeclaration: true,
        showForBusinessName: true,
        showNarration: true,
        showPAN: true,
        showQRCode: true,
        showReceiverSignature: true,
        showSignature: true,
        showTAN: true,
        showTermsAndConditions: true,
        signatureImage: null,
        signatureLabel: 'Authorized Signatory',
        tanLabel: "Company's TAN :",
        termsText: 'Thank you for your business!',
        thermalFooterText: '',
        showThermalFooterText: true
      },
      hsnBreakup: {
        alignment: 'left',
        bodyStyle: {
          borderColor: '#000000',
          fontSize: 9,
        },
        headerStyle: {
          backgroundColor: '#FFFFFF',
          bold: true,
          fontSize: 9,
          textColor: '#000000',
        },
        hsnSACLabel: 'HSNSAC',
        show: true,
        showCGST: true,
        showCGSTAmount: true,
        showCGSTPercent: true,
        showHSNSAC: true,
        showIGST: true,
        showIGSTAmount: true,
        showIGSTPercent: true,
        showSGST: true,
        showSGSTAmount: true,
        showSGSTPercent: true,
        showTaxableValue: true,
        showTotalTax: true,
        taxableValueLabel: 'Taxable Value',
        title: 'HSN Tax Breakup',
      },
      invoiceDetails: {
        alignment: 'right',
        customFields: [],
        destinationLabel: 'Destination',
        detailsLayout: 'table',
        dispatchedThroughLabel: 'Dispatched Through',
        ewayBillLabel: 'e-Way Bill No. & Date',
        industryType: 'Others',
        referenceDateLabel: 'Reference Date',
        referenceNoLabel: 'Reference No.',
        showDestination: true,
        showDispatchedThrough: true,
        showEwayBillDate: true,
        showEwayBillNumber: true,
        showInvoiceDate: true,
        showInvoiceNumber: true,
        showPONumber: false,
        showReferenceDate: true,
        showReferenceNo: true,
        showSODCDate: true,
        showSODCNo: true,
        showTime: false,
        showModeOfPayment: true,
        showTitle: false, // Title is set to false in the provided JSON, but can be enabled if needed
        showVehicleNumber: true,
        sodcDateLabel: 'SO / DC Date',
        sodcNoLabel: 'SO / DC No.',
        title: 'TAX INVOICE',
        titleAlignment: 'center',
        titleFontSize: 9,
      },
      itemTable: {
        alignment: 'left',
        bodyStyle: {
          alternateRowColor: '#F5F5F5',
          borderColor: '#000000',
          fontSize: 10,
          rowHeight: 16,
        },
        columns: [
          {
            key: 'sno',
            label: 'S.No',
            width: 'auto',
            align: 'center',
            show: true,
          },
          {
            key: 'name',
            label: 'Description',
            width: '*',
            align: 'left',
            show: true,
          },
          {
            key: 'hsn',
            label: 'HSN/SAC',
            width: 'auto',
            align: 'center',
            show: true,
          },
          {
            key: 'batch',
            label: 'Batch',
            width: 'auto',
            align: 'center',
            show: true,
          },
          {
            key: 'quantity',
            label: 'Qty',
            width: 'auto',
            align: 'center',
            show: true,
          },
          {
            key: 'unit',
            label: 'Unit',
            width: 'auto',
            align: 'center',
            show: true,
          },
          {
            key: 'price',
            label: 'Rate',
            width: 'auto',
            align: 'right',
            show: true,
          },
          {
            key: 'amount',
            label: 'Amount',
            width: 'auto',
            align: 'right',
            show: true,
          },
          {
            key: 'discount',
            label: 'Discount Amt',
            width: 'auto',
            align: 'right',
            show: true,
          },
          {
            key: 'discountPercent',
            label: 'Discount %',
            width: 'auto',
            align: 'center',
            show: true,
          },
          {
            key: 'taxableAmount',
            label: 'Taxable Amount',
            width: 'auto',
            align: 'right',
            show: true,
          },
          {
            key: 'taxPercent',
            label: 'Tax %',
            width: 'auto',
            align: 'center',
            show: true,
          },
          {
            key: 'taxAmount',
            label: 'Tax Amt',
            width: 'auto',
            align: 'right',
            show: true,
          },
          {
            key: 'total',
            label: 'Total Amount',
            width: 'auto',
            align: 'right',
            show: true,
          },
        ],
        customFields: [],
        headerStyle: {
          backgroundColor: '#FFFFFF',
          bold: true,
          borderColor: '#000000',
          fontSize: 10,
          textColor: '#000000',
        },
        showAlternateUnit: false,
        showBatchNo: false,
        showDiscountAmount: true,
        showDiscountPercent: true,
        showExpDate: false,
        showHSN: true,
        showItemDescription: true,
        showItemTotal: true,
        showMfgDate: true,
        showSerialNumber: true,
        showTaxableAmount: true,
        showTaxAmount: true,
        showTaxPercent: true,
        showTotalQuantityRow: true,
        showUnit: true,
        totalQuantityLabel: 'T.Qty:',
        unitLabel: 'Unit',
      },
      layoutSettings: {
        bankDetailsLayout: 'stacked',
        footerHeight: 80,
        footerLayout: 'two-column',
        headerHeight: 100,
        headerLayout: 'logo-left',
        headerPosition: 'fixed',
        invoiceDetailsLayout: 'two-column',
        itemsPerPage: 20,
        itemsTableLayout: 'standard',
        partyDetailsLayout: 'side-by-side',
        rows: [
          {
            id: 'row-1',
            sections: ['business'],
          },
          {
            id: 'row-2',
            sections: ['invoiceDetails'],
          },
          {
            id: 'row-3',
            sections: ['party'],
          },
          {
            id: 'row-4',
            sections: ['items'],
          },
          {
            id: 'row-5',
            sections: ['totals'],
          },
          {
            id: 'row-6',
            sections: ['amountInWords'],
          },
          {
            id: 'row-7',
            sections: ['hsnBreakup'],
          },
          {
            id: 'row-8',
            sections: ['termsAndConditions'],
          },
          {
            id: 'row-9',
            sections: ['footer'],
          },
        ],
        sectionOrder: [
          {
            id: 'header',
            label: 'Business Details',
            order: 0,
            visible: true,
          },
          {
            id: 'party',
            label: 'Party Details',
            order: 1,
            visible: true,
          },
          {
            id: 'items',
            label: 'Items Table',
            order: 2,
            visible: true,
          },
          {
            id: 'totals',
            label: 'Totals',
            order: 3,
            visible: true,
          },
          {
            id: 'footer',
            label: 'Footer',
            order: 4,
            visible: true,
          },
        ],
        sectionSpacing: 5, // space between sections
        showBusinessDetails: true,
        showFooter: true,
        showInvoiceDetails: false,
        showItemTable: true,
        showPartyDetails: true,
        showTermsAndConditions: true,
        showTotals: true,
        signatureLayout: 'dual',
      },
      metadata: {
        createdAt: '2026-01-10T06:34:05.898Z',
        templateId: '',
        templateName: 'Default Invoice Template',
        updatedAt: '2026-01-12T23:14:58.660Z',
        version: '2.0.0',
      },
      pageSettings: {
        font: 'Roboto',
        borderWidth: 0.5,
        borderStyle: 'solid',
        borderColor: '#000000',
        borderRadius: 0,
        footerMargin: 10,
        headerMargin: 243,
        margins: {
          bottom: 10,
          left: 10,
          right: 10,
          top: 10,
        },
        orientation: 'portrait',
        pageSize: 'A4',
        showBorder: true,
      },
      partyDetails: {
        alignment: 'left',
        billToFields: {
          showCustomerAddress: true,
          showCustomerCity: true,
          showCustomerEmail: true,
          showCustomerGSTIN: true,
          showCustomerMobile: false,
          showCustomerName: true,
          showCustomerPhone: true,
          showCustomerPin: true,
          showCustomerState: true,
          showPartyBalance: false,
        },
        billToPrefix: 'To: M/S.',
        customFields: [],
        dispatchFromFields: {
          showCustomerAddress: true,
          showCustomerCity: true,
          showCustomerEmail: true,
          showCustomerGSTIN: false,
          showCustomerMobile: false,
          showCustomerName: true,
          showCustomerPhone: true,
          showCustomerPin: true,
          showCustomerState: true,
          showPartyBalance: false,
        },
        dispatchFromPrefix: 'Dispatch From',
        layout: 'two-column',
        partyDetailsPosition: 'below-header',
        shipToFields: {
          showCustomerAddress: true,
          showCustomerCity: true,
          showCustomerEmail: true,
          showCustomerGSTIN: true,
          showCustomerMobile: false,
          showCustomerName: true,
          showCustomerPhone: true,
          showCustomerPin: true,
          showCustomerState: true,
          showPartyBalance: false,
        },
        shipToPrefix: 'Consignee (Ship To)',
        showBillTo: true,
        showCustomerAddress: true,
        showCustomerGSTIN: true,
        showCustomerName: true,
        showCustomerPhone: true,
        showDispatchFrom: true,
        showShipTo: false,
      },
      themeSettings: {
        alternateRowColor: '#F5F5F5',
        baseFontSize: 10,
        borderColor: '#000000',
        fontFamily: 'Roboto',
        headerBgColor: '#FFFFFF',
        headerTextColor: '#000000',
        primaryColor: '#000000',
        secondaryColor: '#666666',
        textColor: '#000000',
      },
      totalsSection: {
        alignment: 'right',
        amountInWordsLabel: 'Amount Chargeable (in words) :',
        amountInWordsPosition: 'below-totals',
        amountInWordsPrefix: 'Rupees: ',
        amountInWordsSuffix: ' Only',
        backgroundColor: '#FFFFFF',
        grandTotalBackgroundColor: '#000000',
        grandTotalTextColor: '#FFFFFF',
        layout: 'two-column',
        showAmountInWords: true,
        showCGST: true,
        showDiscount: true,
        showGrandTotal: true,
        showIGST: true,
        showInTableFooter: true,
        showRoundOff: false,
        showSGST: true,
        showSubtotal: true,
        showTaxBreakup: true,
        showTCS: false,
        showTotalQuantity: true,
        tcsLabel: 'TCS',
        textColor: '#000000',
        totalQuantityLabel: 'T.Qty:',
        showBorder: false,
        borderWidth: 0.5,
        borderColor: '#000000',
        showTableBorder: false,
      },
      watermark: {
        angle: -45,
        bold: true,
        color: '#CCCCCC',
        enabled: true,
        fontSize: 60,
        opacity: 0.3,
        position: 'center',
        text: 'DUPLICATE',
      },
    };
  }
}
